import { stickVector } from "./rules";
import type { Vec2 } from "./types";

/** 摇杆和攻击分别跟踪 pointerId，抬起右手不能取消左手的移动。 */
export class InputState {
  keys = new Set<string>();
  stick: Vec2 = { x: 0, y: 0 };
  stickPointer: number | null = null;
  attackPointers = new Set<number>();
  beginStick(id: number): boolean {
    if (this.stickPointer !== null) return false;
    this.stickPointer = id;
    return true;
  }
  moveStick(id: number, dx: number, dy: number): void {
    if (id === this.stickPointer) this.stick = stickVector(dx, dy);
  }
  release(id: number): void {
    this.attackPointers.delete(id);
    if (this.stickPointer === id) {
      this.stickPointer = null;
      this.stick = { x: 0, y: 0 };
    }
  }
  clear(): void {
    this.keys.clear();
    this.stickPointer = null;
    this.stick = { x: 0, y: 0 };
    this.attackPointers.clear();
  }
  get move(): Vec2 {
    const x =
        (this.keys.has("KeyD") || this.keys.has("ArrowRight") ? 1 : 0) -
        (this.keys.has("KeyA") || this.keys.has("ArrowLeft") ? 1 : 0),
      y =
        (this.keys.has("KeyS") || this.keys.has("ArrowDown") ? 1 : 0) -
        (this.keys.has("KeyW") || this.keys.has("ArrowUp") ? 1 : 0);
    return x || y ? { x, y } : this.stick;
  }
  get attacking(): boolean {
    return this.attackPointers.size > 0 || this.keys.has("KeyK");
  }
}

export class Controls {
  readonly state = new InputState();
  touch = false;
  private readonly cleanups: Array<() => void> = [];
  constructor(
    private readonly active: () => boolean,
    private readonly action: (command: string) => void,
  ) {
    this.touch = window.matchMedia("(pointer: coarse)").matches;
    this.syncMode();
    const listen = (
      target: EventTarget,
      event: string,
      fn: EventListener,
    ): void => {
      target.addEventListener(event, fn);
      this.cleanups.push(() => target.removeEventListener(event, fn));
    };
    const mappings: Record<string, string> = {
      Space: "dash",
      KeyE: "interact",
      KeyC: "eat",
      KeyP: "possess",
      KeyQ: "skill",
      KeyZ: "attack-mode",
      KeyX: "guard",
      Tab: "next-companion",
      KeyF: "command-follow",
      KeyG: "command-gather",
      KeyH: "command-guard",
      KeyJ: "command-focus",
      KeyR: "command-retreat",
      KeyB: "bag",
      KeyV: "companions",
    };
    listen(window, "keydown", ((event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement ||
        (event.target instanceof HTMLElement && event.target.isContentEditable)
      )
        return;
      if (event.code === "Escape") {
        this.action("escape");
        return;
      }
      if (!this.active()) return;
      if(event.code==="Tab" && document.activeElement?.id!=="game-canvas")return;
      if (
        mappings[event.code] ||
        [
          "KeyW",
          "KeyA",
          "KeyS",
          "KeyD",
          "KeyK",
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
        ].includes(event.code)
      )
        event.preventDefault();
      this.state.keys.add(event.code);
      if (event.repeat) return;
      if (mappings[event.code]) this.action(mappings[event.code]);
      else if (/^Digit[1-7]$/.test(event.code))
        this.action(`seed-${Number(event.code.slice(-1)) - 1}`);
    }) as EventListener);
    listen(window, "keyup", ((e: KeyboardEvent) => {
      this.state.keys.delete(e.code);
    }) as EventListener);
    listen(window, "blur", (() => {
      this.clear();
      this.action("pause");
    }) as EventListener);
    listen(document, "visibilitychange", (() => {
      if (document.hidden) {
        this.clear();
        this.action("pause");
      }
    }) as EventListener);
    listen(window, "pointerdown", ((e: PointerEvent) => {
      if (e.pointerType === "touch" && !this.touch) {
        this.touch = true;
        this.syncMode();
      }
    }) as EventListener);
    const joystick = document.getElementById("joystick")!,
      knob = document.getElementById("joystick-knob")!;
    const update = (e: PointerEvent): void => {
      const r = joystick.getBoundingClientRect();
      this.state.moveStick(
        e.pointerId,
        e.clientX - r.left - r.width / 2,
        e.clientY - r.top - r.height / 2,
      );
      knob.style.transform = `translate(${this.state.stick.x * 38}px,${this.state.stick.y * 38}px)`;
    };
    listen(joystick, "pointerdown", ((e: PointerEvent) => {
      if (!this.active() || !this.state.beginStick(e.pointerId)) return;
      e.preventDefault();
      joystick.setPointerCapture(e.pointerId);
      update(e);
    }) as EventListener);
    listen(joystick, "pointermove", ((e: PointerEvent) => {
      if (e.pointerId === this.state.stickPointer) update(e);
    }) as EventListener);
    for (const name of ["pointerup", "pointercancel", "lostpointercapture"])
      listen(joystick, name, ((e: PointerEvent) => {
        this.state.release(e.pointerId);
        knob.style.transform = "translate(0,0)";
      }) as EventListener);
    const attack = document.getElementById("attack-button")!;
    listen(attack, "pointerdown", ((e: PointerEvent) => {
      if (!this.active()) return;
      e.preventDefault();
      attack.setPointerCapture(e.pointerId);
      this.state.attackPointers.add(e.pointerId);
    }) as EventListener);
    for (const name of ["pointerup", "pointercancel", "lostpointercapture"])
      listen(attack, name, ((e: PointerEvent) =>
        this.state.release(e.pointerId)) as EventListener);
  }
  private syncMode(): void {
    document.documentElement.classList.toggle("touch-mode", this.touch);
  }
  clear(): void {
    this.state.clear();
    const knob = document.getElementById("joystick-knob");
    if (knob) knob.style.transform = "translate(0,0)";
  }
  destroy(): void {
    for (const clean of this.cleanups) clean();
    this.clear();
  }
}
